/**
 * The workload screen's client — FR-L2, T-2.18.
 *
 * A client per module (waves.md §1.1). Its data is the case module's and is
 * read through `CaseRepo`, so FR-L4 narrows it server-side; **nothing here
 * re-derives who may see what.** The payload carries `scope` — `mine`, `user`
 * or `firm` — which is the server saying what it actually answered, and the
 * screen renders that word rather than assuming.
 */

import { api } from '../api'

export interface WorkloadTask {
  id: string
  case_id: string
  title: string
  due_date: string | null
  stage_key: string | null
  is_required: boolean
}

export interface WorkloadCallback {
  id: string
  subject_kind: string
  subject_id: string
  subject_label: string | null
  scheduled_at: string
  is_late: boolean
}

export interface WorkloadPanel {
  user_id: string | null
  branch_id: string | null
  by_category: Record<string, number>
  open_cases: number
  overdue: number
  tasks_due_this_week: number
  tasks: WorkloadTask[]
  callbacks_today: number
  callbacks: WorkloadCallback[]
}

export interface Branch {
  id: string
  code: string
  name: string
}

export interface WorkloadMeta {
  scope: 'mine' | 'user' | 'firm'
  can_view_all: boolean
  as_at: string
  branches: Branch[]
}

export interface PartnerMeta {
  as_at: string
  firm: {
    by_category: Record<string, number>
    open_cases: number
    unassigned: number
    overdue: number
  }
  /** ProDesk cannot list a firm's staff (ADR-0012 §2), so these are the people
   *  the firm has given work to. The screen says so rather than presenting a
   *  derived list as a directory. */
  pool_is_derived: boolean
  branches: Branch[]
}

export const workload = {
  mine: (branchId?: string) =>
    api<WorkloadPanel>(`/api/v1/workload${branchId ? `?branch_id=${branchId}` : ''}`),
  forUser: (userId: string, branchId?: string) =>
    api<WorkloadPanel>(
      `/api/v1/workload?user_id=${userId}${branchId ? `&branch_id=${branchId}` : ''}`,
    ),
  partners: (branchId?: string) =>
    api<WorkloadPanel[]>(`/api/v1/workload/partners${branchId ? `?branch_id=${branchId}` : ''}`),
}

/**
 * The envelope's `meta`, typed.
 *
 * `api<T>` types `data` and leaves `meta` as `Record<string, unknown>` — a
 * deliberate shape in `lib/api.ts`, which is shared by every module and must
 * not grow a second type parameter for one screen's benefit. These two readers
 * are the narrowing, in one place, with a total fallback: a payload from an
 * older service must render rather than throw.
 */
export function workloadMeta(meta: Record<string, unknown> | undefined): WorkloadMeta {
  return {
    scope: (meta?.scope as WorkloadMeta['scope']) ?? 'mine',
    can_view_all: Boolean(meta?.can_view_all),
    as_at: typeof meta?.as_at === 'string' ? meta.as_at : '',
    branches: Array.isArray(meta?.branches) ? (meta.branches as Branch[]) : [],
  }
}

export function partnerMeta(meta: Record<string, unknown> | undefined): PartnerMeta {
  const firm = (meta?.firm ?? {}) as PartnerMeta['firm']
  return {
    as_at: typeof meta?.as_at === 'string' ? meta.as_at : '',
    firm: {
      by_category: firm.by_category ?? {},
      open_cases: firm.open_cases ?? 0,
      unassigned: firm.unassigned ?? 0,
      overdue: firm.overdue ?? 0,
    },
    pool_is_derived: Boolean(meta?.pool_is_derived),
    branches: Array.isArray(meta?.branches) ? (meta.branches as Branch[]) : [],
  }
}
